import { Injectable, Inject } from '@angular/core';
import { ProjectService } from '../project/project.service';
import { Subject, BehaviorSubject } from 'rxjs';
import { User } from './user.interface';
import { first, filter } from 'rxjs/operators';
import { sortBy } from 'lodash-es';

/**
 * User core service.
 *
 * Contains all the private logic for User management.
 *
 * @todo `user$` should contain all users, but *should be updated when switching project*, because some
 * properties are project specific, such as userRole (project role) and role (application role).
 *
 * **Only UserService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class UserCoreService {

  /** Users subject. */
  protected _user$: Subject<User[]> = new BehaviorSubject<User[]>(null);

  /** Users observable. */
  public user$ = this._user$.asObservable();

  /** Current user subject. */
  protected _current$: Subject<User> = new BehaviorSubject<User>(null);

  /** Current user observable. */
  public current$ = this._current$.asObservable();

  /** Constructor. */
  constructor(
    @Inject('sgAuth') protected sgAuthService: any,
    protected projectService: ProjectService
  ) {
    this._trackProject();
  }


  /**
   * Get users.
   *
   * @returns The current users.
   */
  public get(): User[] {
    let users: User[];
    this.user$.pipe(first()).subscribe(u => users = u);
    return users;
  }

  /**
   * Get specific user.
   *
   * @param id The id of the user.
   * @returns The requested user.
   */
  public getUser(id: User['_id']): User {
    return this.get().find(u => u._id === id);
  }

  /**
   * Get current active user.
   *
   * Shorthand from AuthService for convenience, so all user related code
   * can be accessed from the UserService.
   *
   * @returns The current active user.
   */
  public getUserCurrent(): User {
    let user: User;
    this.current$.pipe(first()).subscribe(u => user = u);
    return user;
  }

  /**
   * Set current user.
   *
   * @param user Set this user as current active user.
   */
  public setUserCurrent(user: User): void {
    this._current$.next(user);
  }

  /**
   * Track project and set users.
   *
   * Users from the Project are patched (flattened) for consistency.
   *
   * @todo Make sure users are updated on project change.
   * @todo Patch for admin users who are not added to the current project,
   *  should be solved by getting all tenant users, not by adding an extra user.
   * @todo Because of the loading order derived from the lgeacy ui-router, there
   *  can be an error where the project is not fully loaded upon init. Should be
   *  'fixed' with `if (!project) { ... }` at the top, but should be removed
   *  after migration to Angular Router, because it's a workaround.
   */
  protected _trackProject(): void {
    this.projectService.project$.pipe(
      filter(project => project !== null)
    ).subscribe(project => {
      const users: User[] = [];
      const currentUser: User = this.sgAuthService.getCurrentUser();

      for (let user of project.users) {
        user = this._patchUser(user);
        user.current = (user._id === currentUser._id);
        users.push(this._patchUser(user));
      }

      // Until tenants are loaded/combined
      if (!users.find(u => u.current)) {
        users.push({
          ...currentUser,
          current: true,
          project: {
            active: true,
            deleted: false,
            role: 'admin'
          }
        });
      }

      this._user$.next(sortBy(users, 'fullName'));
      this._current$.next(users.find(u => u.current));
    });
  }

  /**
   * Patch (flatten) user objects from Project object for consistency.
   *
   * @param user User, either from project or tenant.
   * @returns Patched user.
   */
  protected _patchUser(user: User): User {
    if (typeof user.userId === 'object') {
      user = {
        ...user.userId,
        project: {
          active: user.active,
          deleted: user.deleted,
          role: user.projectRole
        }
      };
    }
    return user;
  }
}
